import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
  createTheme,
  Box,
  withStyles,
  makeStyles,
  Link,
} from "@material-ui/core";
import { NAV_BARS, USER_ROLES } from "../../utils/constants";
import clsx from "clsx";
// Lazy: the Add Lesson dialog carries date pickers and react-select, a
// heavy graph every page was paying to boot. openAddLessonModal already
// awaits data fetches before opening, so the chunk load rides in parallel
// and costs no extra wait; the idle warm below usually has it cached first.
const AddLessonModal = lazy(() => import("../Modal/AddLesson"));
import usersApi from "../../apis/usersApi";
import leadsApi from "../../apis/leadsApi";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import studiosApi from "../../apis/studiosApi";
import SetupPill from "../SetupPill";
import { mainNavItems, publishNav } from "../../utils/mainNav";

const generateClassName = createGenerateClassName({
  productionPrefix: "c",
  disableGlobal: true,
  seed: "nav-bar",
});

const theme = createTheme({
  typography: {
    fontFamily: "'Nunito', 'Varela Round', sans-serif",
  },
  overrides: {
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: "#333333",
        },
      },

    },
    MuiInput: {
      underline: {
        "&::after": {
          borderBottom: "2px solid #333333",
        },
      },
    },
  },
});

const useStyles = makeStyles((theme) => ({
  ul: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    "-ms-overflow-style": "none",
  },
  ulLeft: {
    marginRight: 16,
    [theme.breakpoints.down("sm")]: {
      marginRight: 12,
    },
  },
  // The pill row scrolls with its scrollbar hidden — without a visual cue,
  // off-screen items (My calendar, Bulletins) simply don't exist on phones.
  scrollWrap: {
    position: "relative",
    display: "flex",
    flex: 1,
    minWidth: 0,
  },
  scrollHint: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    background: "linear-gradient(90deg, rgba(246,247,249,0) 0%, var(--ground, #ffffff) 75%)",
    pointerEvents: "none",
  },
  scrollHintButton: {
    pointerEvents: "auto",
    cursor: "pointer",
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "1px solid #E0E0E0",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 4px rgba(0,0,0,.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#1a1a1a",
  },
  li: {
    borderRadius: "140px",
    border: "1px solid #000000",
    backgroundColor: "#ffffff",
    marginRight: 16,
    minWidth: "fit-content",
    cursor: "pointer",
    maxHeight:"52px",
    "&:last-child": {
      marginRight: 0,
    },
  },
  liActive: {
    backgroundColor: "#000000",
  },
  a: {
    textDecoration: "none",
    color: "#000000",
    fontSize: 14,
    lineHeight: "18px",
    fontWeight: 500,
    display: "flex",
    padding: "16px 24px",
    whiteSpace: "nowrap",
  },
  aActive: {
    color: "#ffffff",
  },
  createLessonLi: {
    border: "1px solid #007BFF",
    backgroundColor: "#007BFF",
  },
  createLessonLink: {
    color: "#ffffff",
    alignItems: "center",
  },
  // On phones the pinned pill collapses to a round icon button so the
  // scrolling pill row keeps its room (the wide MY CALENDAR text crowded
  // everything else off-screen).
  calendarLabel: {
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  calendarGlyph: {
    display: "none",
    [theme.breakpoints.down("sm")]: { display: "block", fontSize: 20 },
  },
  calendarLinkMobile: {
    [theme.breakpoints.down("sm")]: { padding: 12 },
  },
}));

const NavBarBox = withStyles((theme) => ({
  root: {
    display: "flex",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      marginTop: 16,
      marginBottom: 16,
    },
  },
}))(Box);

const NavBar = ({ active }) => {
  const current_user = window.current_user || {};
  const STRIPE_CLIENT_ID = window.STRIPE_CLIENT_ID;
  const { role, manage_studio_id, has_studio_completed_oauth, membership_status = "none" } = current_user;
  const classes = useStyles();

  const [isOpenAddLessonModal, setIsOpenAddLessonModal] = useState(false);

  // The side menu, when someone chose it, lights this page's section.
  useEffect(() => {
    publishNav({ active: active || null });
  }, [active]);

  // Warm the Add Lesson chunk during idle so the first click never waits.
  useEffect(() => {
    const warm = () => { import("../Modal/AddLesson"); };
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(warm, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 2500);
    return () => clearTimeout(id);
  }, []);

  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isFetchedStudentAndInstructor, setIsFetchedStudentAndInstructor] =
    useState(false);
  const [addresses, setAddresses] = useState([]);
  const [studioName, setStudioName] = useState("");
  const [lessonMinutes, setLessonMinutes] = useState(45);
  const [dayStartMinutes, setDayStartMinutes] = useState(undefined);
  const navScrollRef = useRef(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Show the "more pills this way" hint only while the row actually has
  // off-screen content to the right.
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return undefined;

    const update = () =>
      setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 8);

    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  });

  const openAddLessonModal = async () => {
    try {
      if (!isFetchedStudentAndInstructor) {
        // Fetch both User students and Lead contacts
        const studentsRes = await usersApi.getStudents();

        console.log('Students fetched:', studentsRes);

        let leadsRes;
        try {
          leadsRes = await leadsApi.getList({ active: true });
          console.log('Leads fetched:', leadsRes);
        } catch (leadError) {
          console.warn('Could not fetch leads:', leadError);
          leadsRes = null;
        }

        const combinedStudents = [];

        // Add User students with type marker
        if (Array.isArray(studentsRes) && studentsRes.length > 0) {
          studentsRes.forEach(student => {
            combinedStudents.push({
              ...student,
              studentType: 'user'
            });
          });
        }

        // Add Lead contacts with type marker
        if (leadsRes?.data && Array.isArray(leadsRes.data) && leadsRes.data.length > 0) {
          leadsRes.data.forEach(lead => {
            combinedStudents.push({
              ...lead,
              studentType: 'lead'
            });
          });
        }

        console.log('Combined students:', combinedStudents);
        setStudents(combinedStudents);
        const instructorsRes = await usersApi.getInstructors();
        if (Array.isArray(instructorsRes) && instructorsRes.length > 0) {
          instructorsRes.unshift({
            id: current_user.id,
            attributes: current_user,
          });
          setInstructors(instructorsRes);
        }

        const studioRes = await studiosApi.getDetail(manage_studio_id);
        if (studioRes.data) {
          const { attributes } = studioRes.data || {};
          setStudioName(attributes.name);
          setLessonMinutes(attributes.lesson_duration_minutes || 45);
          setDayStartMinutes(attributes.day_start_minutes);
        }
        if (
          Array.isArray(studioRes.included) &&
          studioRes.included.length > 0
        ) {
          const addressesFilter = studioRes.included.filter(
            (e) => e.type === "address"
          );
          setAddresses(addressesFilter);
        }
      }

      setIsFetchedStudentAndInstructor(true);
      setIsOpenAddLessonModal(true);
    } catch (error) {
      console.error('Error opening Add Lesson modal:', error);
      setIsFetchedStudentAndInstructor(false);
    }
  };

  return (
    membership_status === "approved" && (
      <StylesProvider generateClassName={generateClassName}>
        <ThemeProvider theme={theme}>
          {/* swyvel-nav: juice.css widens the page container that holds the
              nav on 14 to 16 inch screens. */}
          <NavBarBox className="swyvel-nav">
            <Box className={classes.scrollWrap}>
            <ul ref={navScrollRef} className={clsx(classes.ul, classes.ulLeft)}>
              {/* The same list the side menu draws (utils/mainNav). */}
              {mainNavItems(current_user).map((item) => (
                <li
                  key={item.key}
                  className={clsx(
                    classes.li,
                    active === item.nav ? classes.liActive : ""
                  )}
                >
                  <a
                    href={item.href}
                    className={clsx(
                      classes.a,
                      active === item.nav ? classes.aActive : ""
                    )}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
            {canScrollRight && (
              <Box className={classes.scrollHint}>
                <Box
                  className={classes.scrollHintButton}
                  role="button"
                  aria-label="Show more menu items"
                  onClick={() =>
                    navScrollRef.current &&
                    navScrollRef.current.scrollBy({ left: 220, behavior: "smooth" })
                  }
                >
                  <ChevronRightIcon style={{ fontSize: 18 }} />
                </Box>
              </Box>
            )}
            </Box>
            {/* The pinned CTA slot. Was "Schedule lesson" — 3 uses in the
                app's lifetime, while My calendar (the page owners actually
                live in, with its own create affordances: desktop hover-to-
                create and the mobile floating +) kept getting clipped off
                the end of the scrolling row. Visible at every width. */}
            {role === USER_ROLES.STUDIO &&
              <ul className={classes.ul}>
                <li className={clsx(classes.li, classes.createLessonLi)}>
                  <Link
                    href="/lessons/view_calendar"
                    aria-label="My calendar"
                    className={clsx(classes.a, classes.createLessonLink, classes.calendarLinkMobile)}
                  >
                    <CalendarTodayIcon className={classes.calendarGlyph} />
                    <span className={classes.calendarLabel}>My calendar</span>
                  </Link>
                </li>
              </ul>
            }
          </NavBarBox>


          {isOpenAddLessonModal && (
            <Suspense fallback={null}>
              <AddLessonModal
                isOpen={isOpenAddLessonModal}
                students={students}
                instructors={instructors}
                studio_id={manage_studio_id}
                onClose={() => setIsOpenAddLessonModal(false)}
                addresses={addresses}
                studioName={studioName}
                lessonMinutes={lessonMinutes}
                dayStartMinutes={dayStartMinutes}
              />
            </Suspense>
          )}

          {/* Rides along on every page, since setup work happens on Contacts
              and Classes rather than the dashboard. Renders nothing unless
              window.studio_checklist is present and unfinished. */}
          <SetupPill onScheduleLesson={openAddLessonModal} />
        </ThemeProvider>
      </StylesProvider>
    )
  );
};

export default NavBar;
